import { CircularProgress, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles';
import Uppy from "@uppy/core";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MAXIMUM_MEDIA_FILE_SIZE_BYTES, MAXIMUM_MEDIA_FILE_SIZE_MB } from "../../../constants";
import { baseAtom, removeInterviewByID } from "../../../stores/jotai/base";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import { interviewsAtom } from "../../../stores/jotai/interviews/interviews";
import { cancelRecordingUpload } from "../../../util/cancelRecordingUpload";
import { IrecordRtcUtils } from "../../../util/recordRtcUtils";
import IwButton from "../../app/IwButton";
import TranscriptDropzone from "./TranscriptDropzone";
import TranscriptRecorder from "./TranscriptRecorder";
import { addFileToUppy, createUppyInstance } from "../utils/uppy";

const TRANSCRIPT_UPLOADER_CONTAINER_STYLES = (theme: Theme)=> ({
    alignItems: "center",
    backgroundColor: theme.custom.white,
    padding: "16px",
    borderRadius: "4px",
    border: `1px solid ${theme.custom.borderGrey}`,
});

const TranscriptUploader = () => {
    const [hideDropzone, setHideDropzone] = useState<boolean>(false);
    const [uploadingFile, setUploadingFile] = useState<boolean>(false);
    const [fileIdToUpload, setFileIdToUpload] = useState<string>(null);
    const discoverySession = useAtomValue(discoverySessionAtom);
    const [base, setBase] = useAtom(baseAtom);
    const interviewID = base.selectedInterview;
    const teamID = base.team.groupID;
    const [uppy] = useState<Uppy>(() =>
        createUppyInstance(discoverySession, teamID)
    );
    const [allInterview,]=useAtom(interviewsAtom);

    const interview = useMemo(() => {
        if(allInterview){
            return allInterview?.find((item) => item?.interviewID === interviewID);
        }
    }, [interviewID,allInterview]);

    const fileToUpload = uppy.getFile(fileIdToUpload);
    const theme = useTheme();

    const addFile = async (file) => {
        setUploadingFile(true);
        await addFileToUppy({
          file,
          uppy,
          interviewID,
          setFileIdToUpload,
        });
      };
 

    const onRecordedAudioUpload = (recordingUtilsInstance: IrecordRtcUtils) => {
        const recordingFile = recordingUtilsInstance.getRecordingAsFile(
            interview?.interviewee
        );

        addFile(recordingFile);
    };

    const onTranscriptDrop = (files) => {
        if(!files) return;
        files.forEach((file) => {
            addFile(file);
        });
    };

    const onUpload = () => {
        setUploadingFile(true);
    };

    const onComplete = () => {
        // setUploadingFile(false);
        return;
    };

    const cancelUpload = () => {
        const fileToRemove = uppy.getFile(fileIdToUpload);

        cancelRecordingUpload({
            file: fileToRemove,
            uppy,
            discoverySession,
            teamID,
            onSuccess: () => {
                removeInterviewByID(setBase, fileToRemove.meta.interviewID as string, allInterview);
            },
        })?.finally(() => {
            setUploadingFile(false);
        });
    }

    useEffect(() => {
        uppy.on("upload", onUpload);
        uppy.on("complete", onComplete);

        return () => {
            uppy.off("upload", onUpload);
            uppy.off("complete", onComplete);
        };
    }, [uploadingFile]);

    // useEffect(() => {
    //     if(!fileToUpload?.meta?.s3FileURL) {
    //         setUploadingFile(false);
    //     }
    // }, [fileToUpload?.meta?.s3FileURL])

    return (
        <Stack
            sx={(TRANSCRIPT_UPLOADER_CONTAINER_STYLES(theme))}
            spacing={3}
        >
            {uploadingFile ? (
                <>
                    <Typography
                        fontWeight={400}
                        align="center"
                    >
                        Uploading your file. Please, wait for a while.
                    </Typography>
                    <CircularProgress />
                    <IwButton
                        variant="outlined"
                        size="small"
                        onClick={cancelUpload}
                        disabled={!fileToUpload?.meta?.s3FileURL}
                    >
                        Cancel Upload
                    </IwButton>
                </>
            ) : (
                <>
                    {!hideDropzone && (
                        <Typography
                            fontWeight={400}
                            align="center"
                        >
                            Please upload an audio or video file to continue with transcription.
                        </Typography>
                    )}
                    <TranscriptRecorder
                        onShowRecordPlayer={() => setHideDropzone(true)}
                        onHideRecordPlayer={() => setHideDropzone(false)}
                        onUpload={onRecordedAudioUpload}
                        isTeamMember={base?.roleSnapshot?.isTeamMember}
                    />
                    {!hideDropzone && (
                        <>
                            <Typography
                                fontWeight={400}
                                align="center"
                            >
                                Or
                            </Typography>
                            <TranscriptDropzone onDrop={onTranscriptDrop} isTeamMember={base?.roleSnapshot?.isTeamMember}/>
                        </>
                    )}
                </>
            )}
        </Stack>
    );
};

export default TranscriptUploader;
